import { Component, OnInit, Output, EventEmitter, OnDestroy, NgZone } from '@angular/core';

import { FeatureStatus } from 'src/app/data-models/common/feature-status.model';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { CommonService } from 'src/app/services/common/common.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { AppNotification } from 'src/app/data-models/common/app-notification.model';
import { DeviceMonitorStatus } from 'src/app/enums/device-monitor-status.enum';
import { DisplayService } from 'src/app/services/display/display.service';
import { DeviceService } from 'src/app/services/device/device.service';
import { LocalStorageKey } from 'src/app/enums/local-storage-key.enum';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { EMPTY } from 'rxjs';
import { VantageShellService } from 'src/app/services/vantage-shell/vantage-shell.service';
import { Router } from '@angular/router';
import { MicrophoneService } from '../../../services/audio/microphone/microphone.service';

@Component({
	selector: 'vtr-widget-quicksettings',
	templateUrl: './widget-quicksettings.component.html',
	styleUrls: ['./widget-quicksettings.component.scss'],
})
export class WidgetQuicksettingsComponent implements OnInit, OnDestroy {
	public microphoneStatus = new FeatureStatus(false, false);
	private notificationSubscription: Subscription;
	public microPhoneGreyOut = false;
	public isMicrophoneVisible = false;
	public quickSettingsWidget = [
		{
			// tooltipText: 'MICROPHONE',
			state: true,
		},
		{
			// tooltipText: 'CAMERA PRIVACY',
			state: true,
		},
		{
			// tooltipText: 'EYE CARE MODE',
			state: true,
		},
	];
	private Windows: any;
	private windowsObj: any;
	private audioClient: any;
	private audioData: string;
	private microphoneDevice: any;
	private microphonePermissionHandler: any;
	private isMicrophoneFeatureToggleEnabled = true;
	private loadedStatus = {
		microphone: false,
	};
	@Output() toggle = new EventEmitter<{ sender: string; value: boolean }>();
	@Output() quickSettingsDeviceAvailability = new EventEmitter<{
		microphoneAvailable: boolean;
	}>();

	constructor(
		public dashboardService: DashboardService,
		public displayService: DisplayService,
		private commonService: CommonService,
		private logger: LoggerService,
		public deviceService: DeviceService,
		private ngZone: NgZone,
		private vantageShellService: VantageShellService,
		private router: Router,
		private microphoneService: MicrophoneService
	) {
		this.microphoneStatus.isLoading = false;

		this.Windows = vantageShellService.getWindows();
		if (this.Windows) {
			this.windowsObj =
				this.Windows.Devices.Enumeration.DeviceAccessInformation.createFromDeviceClass(
					this.Windows.Devices.Enumeration.DeviceClass.videoCapture
				);

			this.microphoneDevice =
				this.Windows.Devices.Enumeration.DeviceAccessInformation.createFromDeviceClass(
					this.Windows.Devices.Enumeration.DeviceClass.audioCapture
				);
		}
	}

	ngOnInit() {
		this.initFeatureToggles();
		this.notificationSubscription = this.commonService.notification.subscribe(
			(response: AppNotification) => {
				this.onNotification(response);
			}
		);

		this.initFeatures();

		if (this.microphoneDevice) {
			this.microphonePermissionHandler = (args: any) => {
				if (args && args.status != null) {
					switch (args.status) {
						case 0:
						case 1:
							this.updateMicrophoneStatus();
							break;
						case 2:
						case 3:
							this.microphoneStatus.permission = false;
							break;
					}
				}
			};
			this.microphoneDevice.addEventListener(
				'accesschanged',
				this.microphonePermissionHandler,
				false
			);
		}
	}

	initFeatureToggles() {
		this.isMicrophoneFeatureToggleEnabled = this.microphoneService.getFeatureToggle();
	}

	ngOnDestroy() {
		if (this.notificationSubscription) {
			this.notificationSubscription.unsubscribe();
		}
		if (this.audioClient) {
			try {
				this.audioClient.stopMonitor();
			} catch (error) {
				this.logger.error('core audio stop moniotr error ' + error.message);
			}
		} else {
			this.deviceService.stopMicrophoneMonitor();
		}
		this.deviceService.stopMicrophoneMonitor();
		if (this.microphoneDevice) {
			this.microphoneDevice.removeEventListener(
				'accesschanged',
				this.microphonePermissionHandler,
				false
			);
		}
	}

	//#region private functions
	// DeviceMonitorStatus
	private onNotification(notification: AppNotification) {
		if (notification) {
			const { type, payload } = notification;
			switch (type) {
				case DeviceMonitorStatus.MicrophoneStatus:
					this.logger.info(
						'DeviceMonitorStatus.MicrophoneStatus ' + JSON.stringify(payload)
					);
					this.ngZone.run(() => {
						// microphone payload data is dynamic, need check one by one
						if (
							payload.hasOwnProperty('muteDisabled') &&
							this.microphoneStatus.permission
						) {
							this.microphoneStatus.status = payload.muteDisabled;
						}
						if (payload.hasOwnProperty('permission')) {
							this.microphoneStatus.permission = payload.permission;
						}
						if (payload.hasOwnProperty('available')) {
							this.microphoneStatus.available = payload.available === true;
							this.emitMicrophoneAvailability();
						}
					});
					break;
				case LocalStorageKey.WelcomeTutorial:
					if (payload.page === 2) {
						this.initFeatures();
					}
					break;
				default:
					break;
			}
		}
	}

	private async initFeatures() {
		this.getMicrophoneStatus();
	}

	private verifyMicrophoneVisibility() {
		this.isMicrophoneVisible =
			this.isMicrophoneFeatureToggleEnabled && this.microphoneStatus.available;
		this.emitMicrophoneAvailability();
	}

	private getMicrophoneStatus() {
		if (this.dashboardService.isShellAvailable) {
			this.loadedStatus.microphone = false;
			this.dashboardService
				.getMicrophoneStatus()
				.then((featureStatus) => {
					this.loadedStatus.microphone = true;
					this.setMicrophoneStatus(featureStatus);
					this.microPhoneGreyOut = false;
					if (featureStatus.available) {
						const win: any = window;
						if (win.VantageShellExtension && win.VantageShellExtension.AudioClient) {
							try {
								this.audioClient =
									win.VantageShellExtension.AudioClient.getInstance();

								if (this.audioClient) {
									this.audioClient.onchangecallback = (data: string) => {
										if (data) {
											if (
												this.audioData &&
												this.audioData.toString() === data
											) {
												return;
											}
											this.logger.info('data data, got it ' + data);
											this.audioData = data;
											const dic = data.split(',');

											if (['1', '0'].includes(dic[0])) {
												const muteDisabled = dic[0] === '0';
												this.commonService.sendNotification(
													DeviceMonitorStatus.MicrophoneStatus,
													{ muteDisabled }
												);
											} else {
												this.logger.info('core audio wrong data format');
											}
										}
									};
								}
								this.audioClient.startMonitor();
							} catch (error) {
								this.logger.error(
									'cannot init core audio for widget quick settings' +
										error.message
								);
							}
						} else {
							this.logger.info('current shell version maybe not support core audio');
						}
					}
				})
				.catch((error) => {
					this.loadedStatus.microphone = true;
					this.logger.error('getMicrophoneStatus', error.message);
					return EMPTY;
				});
		}
	}

	private setMicrophoneStatus(featureStatus: FeatureStatus) {
		this.microphoneStatus = { ...this.microphoneStatus, ...featureStatus };
		this.verifyMicrophoneVisibility();
	}

	public onMicrophoneStatusToggle(toggleValue: boolean) {
		this.microphoneStatus.isLoading = true;
		this.quickSettingsWidget[0].state = false;
		try {
			if (this.dashboardService.isShellAvailable) {
				this.dashboardService
					.setMicrophoneStatus(toggleValue)
					.then((value: boolean) => {
						this.microphoneStatus.isLoading = false;
						this.microphoneStatus.status = toggleValue;
						this.quickSettingsWidget[0].state = true;
						setTimeout(() => {
							const microphoneDom = document.getElementById(
								'Microphone_checkbox'
							) as HTMLElement;
							microphoneDom.focus();
						}, 10);
					})
					.catch((error) => {
						this.microphoneStatus.isLoading = false;
						this.quickSettingsWidget[0].state = true;
						this.logger.error('setMicrophoneStatus', error.message);
						return EMPTY;
					});
			}
		} catch (error) {
			this.microphoneStatus.isLoading = false;
			this.quickSettingsWidget[0].state = true;
			return EMPTY;
		}
	}

	public onSystemUpdateToggle($event: boolean) {
		this.router.navigate(['device/system-updates'], { queryParams: { action: 'start' } });
	}

	onPermissionNoteClick(path: string) {
		this.deviceService.launchUri(path);
	}

	private updateMicrophoneStatus() {
		if (this.dashboardService.isShellAvailable) {
			this.dashboardService
				.getMicrophoneStatus()
				.then((featureStatus: any) => {
					this.setMicrophoneStatus(featureStatus);
				})
				.catch((error) => {
					this.logger.error('getMicrophoneStatus', error.message);
				});
		}
	}

	private emitMicrophoneAvailability() {
		if (this.loadedStatus.microphone) {
			this.quickSettingsDeviceAvailability.emit({
				microphoneAvailable: this.isMicrophoneVisible,
			});
		}
	}
}
